/**
 * @fileoverview Month calendar view for the timesheet (MOR-906).
 *
 * A Mon–Sun month grid: each day cell shows the recorded hours and up to
 * three category colour dots, so a month of timekeeping reads at a glance.
 * Clicking a day jumps to that day's list view for editing. Weekend cells
 * are dimmed; days outside the month are muted.
 */

import { useMemo } from "react";
import {
  addDays,
  endOfMonth,
  format,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import type { Id } from "@cvx/_generated/dataModel";
import { cn } from "@/utils/misc";
import { TASK_COLOR_DOT } from "@/components/settings/tasks/task-colors";
import type { TaskColor } from "@cvx/lib/taskColor.schema";

interface MonthEntry {
  date: string;
  units: number;
  categoryId: Id<"timeCategories">;
}

export function TimesheetMonthView({
  monthAnchor,
  entries,
  todayIso,
  fmtHours,
  categoryColor,
  onDayClick,
}: {
  /** Any ISO date inside the month to render. */
  monthAnchor: string;
  entries: MonthEntry[];
  todayIso: string;
  fmtHours: (units: number) => string;
  categoryColor: (categoryId: Id<"timeCategories">) => TaskColor | undefined;
  onDayClick: (date: string) => void;
}) {
  const anchor = parseISO(monthAnchor);

  const weeks = useMemo(() => {
    const anchorDate = parseISO(monthAnchor);
    const out: string[][] = [];
    let cursor = startOfWeek(startOfMonth(anchorDate), { weekStartsOn: 1 });
    const monthEnd = endOfMonth(anchorDate);
    while (cursor <= monthEnd || out.length === 0) {
      const week: string[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(format(addDays(cursor, i), "yyyy-MM-dd"));
      }
      out.push(week);
      cursor = addDays(cursor, 7);
    }
    return out;
  }, [monthAnchor]);

  const byDay = useMemo(() => {
    const map = new Map<string, { units: number; categoryIds: Set<string> }>();
    for (const e of entries) {
      const day = map.get(e.date) ?? { units: 0, categoryIds: new Set() };
      day.units += e.units;
      day.categoryIds.add(e.categoryId as string);
      map.set(e.date, day);
    }
    return map;
  }, [entries]);

  return (
    <div className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
      <div className="grid grid-cols-7 border-b border-border/60">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label) => (
          <div
            key={label}
            className="px-2 py-2 text-center text-xs font-medium text-muted-foreground"
          >
            {label}
          </div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div
          key={wi}
          className="grid grid-cols-7 border-b border-border/40 last:border-b-0"
        >
          {week.map((day) => {
            const date = parseISO(day);
            const inMonth = isSameMonth(date, anchor);
            const info = byDay.get(day);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            return (
              <button
                key={day}
                type="button"
                onClick={() => onDayClick(day)}
                className={cn(
                  "flex min-h-20 flex-col items-start gap-1 border-l border-border/40 p-2 text-left transition-colors first:border-l-0 hover:bg-muted",
                  !inMonth && "bg-muted/30 text-muted-foreground/50",
                  inMonth && isWeekend && "bg-muted/10",
                )}
              >
                <span
                  className={cn(
                    "text-xs tabular-nums",
                    day === todayIso &&
                      "flex size-5 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground",
                  )}
                >
                  {format(date, "d")}
                </span>
                {info && (
                  <>
                    <span className="text-xs font-medium tabular-nums">
                      {fmtHours(info.units)}
                    </span>
                    <span className="flex gap-0.5">
                      {[...info.categoryIds].slice(0, 3).map((categoryId) => (
                        <span
                          key={categoryId}
                          className={cn(
                            "size-1.5 rounded-full",
                            TASK_COLOR_DOT[
                              categoryColor(
                                categoryId as Id<"timeCategories">,
                              ) ?? "gray"
                            ],
                          )}
                        />
                      ))}
                      {info.categoryIds.size > 3 && (
                        <span className="text-[9px] leading-none text-muted-foreground">
                          +{info.categoryIds.size - 3}
                        </span>
                      )}
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
